import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Loader2, MailCheck } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 sm:px-6 py-10 lg:py-16">
      <div className="w-full max-w-[400px]">
        {sent ? (
          <div className="text-center" data-testid="forgot-password-sent">
            <div className="w-14 h-14 rounded-full bg-paw-success/10 flex items-center justify-center mx-auto mb-4">
              <MailCheck className="w-7 h-7 text-paw-success" strokeWidth={2} />
            </div>
            <h1 className="font-outfit font-bold text-2xl text-paw-text tracking-tight mb-2">Check your email</h1>
            <p className="font-manrope text-sm text-paw-text-muted">
              If an account exists for <strong className="text-paw-text">{email}</strong>, we've sent a link to reset your password. The link expires in 30 minutes.
            </p>
            <p className="font-manrope text-xs text-paw-warning mt-3">Don't see it? Check your spam / junk folder.</p>
            <Link to="/login" className="inline-block mt-7 font-manrope text-sm text-paw-accent font-semibold hover:underline">Back to log in</Link>
          </div>
        ) : (
          <>
            <div className="mb-10">
              <h1 className="font-outfit font-bold text-3xl text-paw-text tracking-tight mb-2">Forgot your password?</h1>
              <p className="font-manrope text-sm text-paw-text-muted">Enter your email and we'll send you a link to reset it.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-paw-error/8 border border-paw-error/15 text-paw-error text-sm font-manrope rounded-xl px-4 py-3" data-testid="forgot-password-error">{error}</div>
              )}
              <div className="space-y-1.5">
                <Label className="font-manrope text-sm font-medium text-paw-text">Email</Label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11 rounded-xl border-paw-border bg-white focus:border-paw-primary focus:ring-paw-primary/20" data-testid="forgot-password-email-input" />
              </div>
              <button type="submit" disabled={loading} className="w-full bg-paw-primary hover:bg-paw-primary-hover disabled:opacity-60 text-white rounded-xl h-11 font-manrope font-semibold text-sm transition-colors flex items-center justify-center gap-2" data-testid="forgot-password-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send reset link <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>

            <p className="text-center mt-8 font-manrope text-sm text-paw-text-muted">
              Remembered it? <Link to="/login" className="text-paw-accent font-semibold hover:underline" data-testid="forgot-password-login-link">Log in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
